import React, { FC } from 'react';
import { Alert, Typography } from '@mui/material';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../components/store/Store';
import SettingsInputContainer from '../../../components/SettingsInputContainer';
import TextInput from '../../../components/forms/TextInput';
import Container from '../../../components/Container';
import CheckboxInput from '../../../components/forms/CheckboxInput';
import SettingsStatusAlert from './SettingsStatusAlert';
import { Site } from '../../../declarations/models/Site';

export const WebpageTrackingSettings: FC = () => {
  const { t } = useTranslation('components');
  const { state } = useStore();
  const isSuperAdmin = state.currentUser?.user?.is_super_admin;

  const {
    field: { value: enablePlausible },
  } = useController<Site, `content.settings.enablePlausible`>({
    name: `content.settings.enablePlausible`,
  });

  return (
    <>
      <SettingsInputContainer title={t(`Settings.WebpageSettings.WebpageTrackingSettings.plausible`)}>
        <Container fullWidth spaceBetween gap={2}>
          <Container column left fullWidth gap={2}>
            <Typography>{t(`Settings.WebpageSettings.WebpageTrackingSettings.plausibleDescription`)}</Typography>

            <SettingsStatusAlert
              active={Boolean(enablePlausible)}
              activeText={t('Settings.WebpageSettings.WebpageTrackingSettings.plausibleEnabled')}
              inactiveText={t('Settings.WebpageSettings.WebpageTrackingSettings.plausibleNotEnabled')}
            />

            {isSuperAdmin && (
              <div className='superadmin' style={{ backgroundColor: '#fbe6ff', padding: '1em', width: '100%' }}>
                <Container column left fullWidth>
                  <CheckboxInput
                    fullWidth={false}
                    path='content.settings.enablePlausible'
                    label={t('Settings.WebpageSettings.WebpageTrackingSettings.plausibleEnable')}
                  />
                  <TextInput
                    path='content.settings.plausibleWebsiteId'
                    defaultValue=''
                    label={t(`Settings.WebpageSettings.WebpageTrackingSettings.plausibleWebsiteId`)}
                  />
                  <TextInput
                    path='content.settings.plausibleScriptId'
                    defaultValue=''
                    label={t(`Settings.WebpageSettings.WebpageTrackingSettings.plausibleScriptId`)}
                  />
                  <TextInput
                    path='content.settings.plausibleAuth'
                    defaultValue=''
                    label={t(`Settings.WebpageSettings.WebpageTrackingSettings.plausibleAuth`)}
                    placeholder={t(`Settings.WebpageSettings.WebpageTrackingSettings.plausibleAuth`)}
                  />
                  <CheckboxInput
                    fullWidth={false}
                    path='content.settings.plausibleUniqueDomain'
                    label={t('Settings.WebpageSettings.WebpageTrackingSettings.plausibleUniqueDomain')}
                  />
                  <CheckboxInput
                    fullWidth={false}
                    path='content.settings.plausibleEnableLocalhostTracking'
                    label={t('Settings.WebpageSettings.WebpageTrackingSettings.plausibleEnableLocalhostTracking')}
                  />
                </Container>
              </div>
            )}
          </Container>
        </Container>
      </SettingsInputContainer>

      <SettingsInputContainer title={t(`Settings.WebpageSettings.WebpageTrackingSettings.google`)}>
        <Alert severity='warning' sx={{ width: '100%' }}>
          {t(`Settings.WebpageSettings.WebpageTrackingSettings.onlyKeysWarning`)}
        </Alert>
        <Container fullWidth spaceBetween gap={2}>
          <Container column left fullWidth>
            <Typography variant='subtitle2'>
              {t(`Settings.WebpageSettings.WebpageTrackingSettings.googleGtagKey`)}
            </Typography>
            <TextInput path='content.settings.googleGtagKey' defaultValue='' label='' placeholder='G-XXXXXXXXXX' />
          </Container>
          <Container column left fullWidth>
            <Typography variant='subtitle2'>
              {t(`Settings.WebpageSettings.WebpageTrackingSettings.googleTagmanagerKey`)}
            </Typography>
            <TextInput path='content.settings.googleTagmanagerKey' defaultValue='' label='' placeholder='GTM-XXXXXXX' />
          </Container>
        </Container>

        <Container fullWidth spaceBetween gap={2}>
          <Container column left fullWidth>
            <Typography variant='subtitle2'>
              {t(`Settings.WebpageSettings.WebpageTrackingSettings.googleSiteVerification`)}
            </Typography>
            <TextInput
              path='content.settings.googleSiteVerification'
              defaultValue=''
              label=''
              placeholder='aB3dE5fG7hJ9kL1mN3pQ5rS7tU9vW1xY3zA5bC7dE9'
            />
            <Typography variant='caption'>
              {t(`Settings.WebpageSettings.WebpageTrackingSettings.googleSiteVerificationCaption`)}
            </Typography>
          </Container>
          <Container column left fullWidth>
            <Typography variant='subtitle2'>
              {t(`Settings.WebpageSettings.WebpageTrackingSettings.googleMapsKey`)}
            </Typography>
            <TextInput
              path='content.settings.googleMapsKey'
              defaultValue=''
              label=''
              placeholder={t(`Settings.WebpageSettings.WebpageTrackingSettings.googleMapsKeyPlaceholder`)}
            />
          </Container>
        </Container>
        <Container fullWidth spaceBetween gap={2} column left>
          <CheckboxInput
            fullWidth={false}
            path='content.settings.enableStructuredGoogleBreadcrumbs'
            label={t('Settings.WebpageSettings.WebpageTrackingSettings.enableStructuredGoogleBreadcrumbs')}
          />
          <CheckboxInput
            fullWidth={false}
            path='content.settings.enableStructuredGoogleEvents'
            label={t('Settings.WebpageSettings.WebpageTrackingSettings.enableStructuredGoogleEvents')}
          />
        </Container>
      </SettingsInputContainer>

      <SettingsInputContainer title={t(`Settings.WebpageSettings.WebpageTrackingSettings.facebook`)}>
        <Alert severity='warning' sx={{ width: '100%' }}>
          {t(`Settings.WebpageSettings.WebpageTrackingSettings.onlyKeysWarning`)}
        </Alert>
        <Container fullWidth spaceBetween gap={2}>
          <Container column left fullWidth>
            <Typography variant='subtitle2'>
              {t(`Settings.WebpageSettings.WebpageTrackingSettings.facebookPixcelCode`)}
            </Typography>
            <TextInput
              path='content.settings.facebookPixcelCode'
              defaultValue=''
              label=''
              placeholder={t(`Settings.WebpageSettings.WebpageTrackingSettings.facebookPixcelCodePlaceholder`)}
            />
            <Typography variant='caption'>
              {t(`Settings.WebpageSettings.WebpageTrackingSettings.facebookPixcelCodeCaption`)}
            </Typography>
          </Container>
          <Container column left fullWidth>
            <Typography variant='subtitle2'>
              {t(`Settings.WebpageSettings.WebpageTrackingSettings.facebookDomainVerification`)}
            </Typography>
            <TextInput
              path='content.settings.facebookDomainVerification'
              defaultValue=''
              label=''
              placeholder={t(`Settings.WebpageSettings.WebpageTrackingSettings.facebookDomainVerificationPlaceholder`)}
            />
            <Typography variant='caption'>
              {t(`Settings.WebpageSettings.WebpageTrackingSettings.facebookDomainVerificationCaption`)}
            </Typography>
          </Container>
        </Container>
      </SettingsInputContainer>
    </>
  );
};

export default WebpageTrackingSettings;
